<template>
  <div>
    <base-input
      v-if="mode === 'typed'"
      :id="inputId"
      :autocomplete="autocomplete"
      :error="error"
      :secondary-color="textColor"
      :input-style="{ 'color': textColor }"
      v-mask="dateMask"
      :placeholder="datePlaceholder"
      :value="value"
      @input="$emit('input', $event)"
    ></base-input>

    <div v-else-if="mode === 'dropdowns'" class="birthday-selects" :style="{ color: textColor, '--birthday-panel-bg': cardColor || '#ffffff' }">
      <div class="row">
        <div v-for="(part, idx) in partOrder" :key="part"
             :class="[part === 'month' ? 'col-5' : (part === 'day' ? 'col-3' : 'col-4'),
                      idx === 0 ? 'pr-1' : (idx === 2 ? 'pl-1' : 'px-1')]">
          <base-input v-if="part === 'day'" :secondary-color="textColor">
            <el-select
              v-model="day"
              :popper-append-to-body="false"
              :placeholder="$t('checkin.day')"
              @change="emitValue"
            >
              <el-option
                v-for="d in dayOptions"
                :label="d"
                :value="d"
                :key="'d' + d"
              ></el-option>
            </el-select>
          </base-input>
          <base-input v-else-if="part === 'month'" :secondary-color="textColor">
            <el-select
              v-model="monthIndex"
              :popper-append-to-body="false"
              :placeholder="$t('checkin.month')"
              @change="onMonthOrYearChange"
            >
              <el-option
                v-for="(m, idx) in monthNames"
                :label="m"
                :value="idx + 1"
                :key="'m' + idx"
              ></el-option>
            </el-select>
          </base-input>
          <base-input v-else :secondary-color="textColor">
            <el-select
              v-model="year"
              :popper-append-to-body="false"
              :placeholder="$t('checkin.year')"
              @change="onMonthOrYearChange"
            >
              <el-option
                v-for="y in yearOptions"
                :label="y"
                :value="y"
                :key="'y' + y"
              ></el-option>
            </el-select>
          </base-input>
        </div>
      </div>
      <div v-if="error" class="form-group has-danger birthday-error">
        <label class="error">{{ error }}</label>
      </div>
    </div>

    <div v-else class="birthday-calendar" :style="{ color: textColor }">
      <base-input :id="inputId" :error="error" :secondary-color="textColor">
        <el-date-picker
          type="date"
          :editable="false"
          :clearable="true"
          prefix-icon="' '"
          value-format="yyyy-MM-dd"
          :format="elFormat"
          :placeholder="datePlaceholder"
          :value="value || null"
          :picker-options="calendarPickerOptions"
          @input="$emit('input', $event || '')"
        ></el-date-picker>
      </base-input>
    </div>
  </div>
</template>
<script>
import { isValidIsoDate } from '@/util/birthdayDate'

export default {
  name: 'birthday-input',
  props: {
    mode: {
      type: String,
      default: 'typed'
    },
    value: {
      type: String,
      default: ''
    },
    dateMask: {
      type: [String, Array],
      default: '##/##/####'
    },
    datePlaceholder: {
      type: String,
      default: 'MM/DD/YYYY'
    },
    dateFormat: {
      type: String,
      default: 'MM/dd/yyyy'
    },
    firstDayOfWeek: {
      type: Number,
      default: 1
    },
    error: {
      type: String,
      default: ''
    },
    textColor: {
      type: String,
      default: ''
    },
    cardColor: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: null
    },
    autocomplete: {
      type: String,
      default: 'bday'
    }
  },
  data() {
    return {
      day: '',
      monthIndex: '',
      year: ''
    }
  },
  computed: {
    monthNames() {
      return this.$t('dates.months')
    },
    partOrder() {
      const first = (this.dateFormat || '').charAt(0).toLowerCase()
      if (first === 'd') return ['day', 'month', 'year']
      if (first === 'y') return ['year', 'month', 'day']
      return ['month', 'day', 'year']
    },
    yearOptions() {
      const current = new Date().getFullYear()
      const years = []
      for (let y = current; y >= current - 120; y--) {
        years.push(y)
      }
      return years
    },
    dayOptions() {
      const days = []
      for (let d = 1; d <= this.maxDayForSelection; d++) {
        days.push(d)
      }
      return days
    },
    maxDayForSelection() {
      if (!this.monthIndex) return 31
      if ([1, 3, 5, 7, 8, 10, 12].includes(this.monthIndex)) return 31
      if (this.monthIndex !== 2) return 30
      if (!this.year) return 29
      const leap = (this.year % 4 === 0 && this.year % 100 !== 0) || this.year % 400 === 0
      return leap ? 29 : 28
    },
    elFormat() {
      // lowercase only the year and day tokens for element-ui; month-name
      // tokens (MMM/MMMM) pass through and render via the element locale
      return (this.dateFormat || 'MM/dd/yyyy').replaceAll('Y', 'y').replaceAll('D', 'd')
    },
    calendarPickerOptions() {
      return {
        // group setting is 0-6 (Sunday=0); Element UI wants 1-7 (Sunday=7)
        firstDayOfWeek: this.firstDayOfWeek === 0 ? 7 : (this.firstDayOfWeek || 1),
        disabledDate(date) {
          return date > new Date()
        }
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.mode !== 'dropdowns') return
        if (isValidIsoDate(val)) {
          const [y, m, d] = val.split('-').map(Number)
          this.year = y
          this.monthIndex = m
          this.day = d
        } else if (!val) {
          this.year = ''
          this.monthIndex = ''
          this.day = ''
        }
      }
    }
  },
  methods: {
    onMonthOrYearChange() {
      if (this.day && this.day > this.maxDayForSelection) {
        this.day = ''
      }
      this.emitValue()
    },
    emitValue() {
      if (this.day === '' || this.monthIndex === '' || this.year === '') {
        this.$emit('input', '')
        return
      }
      const mm = ('0' + this.monthIndex).slice(-2)
      const dd = ('0' + this.day).slice(-2)
      this.$emit('input', this.year + '-' + mm + '-' + dd)
    }
  }
}
</script>
<style>
/* Match the waiver's themed inputs: border from the config-driven CSS var,
   text and caret from the card text color, no library accent colors. */
.birthday-selects .el-input__inner,
.birthday-selects .el-select .el-input .el-input__inner,
.birthday-calendar .el-input__inner {
  background-color: transparent !important;
  border-color: var(--input-border-color, #7d7d7d99) !important;
  color: inherit !important;
}
.birthday-selects .el-input.is-focus .el-input__inner,
.birthday-selects .el-input__inner:focus,
.birthday-calendar .el-input.is-focus .el-input__inner,
.birthday-calendar .el-input__inner:focus {
  border-color: var(--input-border-color, #7d7d7d99);
}
.birthday-selects .el-input__inner::placeholder,
.birthday-calendar .el-input__inner::placeholder {
  color: inherit !important;
  opacity: 0.7;
}
.birthday-selects .el-input__suffix,
.birthday-calendar .el-input__suffix {
  display: flex;
  align-items: center;
}
/* The theme's _plugin-datepicker.scss styles .el-date-editor.el-input
   .el-input__inner with !important (grey border, fixed grey text), so the
   calendar override needs higher specificity plus !important to win. */
.birthday-calendar .el-date-editor.el-input .el-input__inner {
  border-color: var(--input-border-color, #7d7d7d99) !important;
  color: inherit !important;
  background-color: transparent !important;
}
.birthday-calendar .el-date-editor.el-input .el-input__inner::placeholder {
  color: inherit !important;
  opacity: 0.7;
}
/* Clear (x) icon on the calendar input takes the configured border color,
   matching the select carets. Blanked prefix icon keeps text left-aligned. */
.birthday-calendar .el-input__icon,
.birthday-calendar .el-date-editor.el-input .el-input__icon,
.birthday-calendar .el-date-editor.el-input .el-input__suffix .el-input__icon {
  color: var(--input-border-color, #7d7d7d99) !important;
}
.birthday-calendar .el-input--prefix .el-input__inner {
  padding-left: 15px;
}
.birthday-selects .el-select__caret {
  color: var(--input-border-color, #7d7d7d99) !important;
  opacity: 1;
  font-size: 14px;
}
/* Pull the error label up tight under the selects (their form-groups carry
   10px bottom margin) and let its own form-group margin space the next field. */
.birthday-error {
  margin-top: -6px;
}
.birthday-error .error {
  margin-bottom: 0;
}
/* Panel renders in-component (popper-append-to-body off) so these apply.
   Neutral grays for selection and hover, no theme accent. */
.birthday-selects .el-select-dropdown__item.selected {
  color: inherit;
  font-weight: 600;
  background-color: rgba(125, 125, 125, 0.15);
}
.birthday-selects .el-select-dropdown__item.hover,
.birthday-selects .el-select-dropdown__item:hover {
  background-color: rgba(125, 125, 125, 0.1);
}
.birthday-selects .el-select-dropdown__item {
  color: inherit;
}
.birthday-selects .el-select-dropdown {
  background-color: var(--birthday-panel-bg, #fff);
  border-color: var(--input-border-color, #7d7d7d99);
}
</style>
